import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { plants } from "@/lib/api";
import { compressImage, readFileAsDataURL } from "@/lib/imageCompress";
import { MAX_BULK, parseTagList, getLocationOnce } from "@/lib/cameraUtils";
import { safeExifDateTaken } from "@/lib/exif";
import { useIdentifyRetryQueue } from "@/hooks/useIdentifyRetryQueue";
import { useMilestonesContext } from "@/contexts/MilestonesContext";
import { recordIdentifyDurations } from "@/lib/identifyEta";

// Concurrency of the bulk-identify worker pool. Kept small so we stay under
// the per-user 30/60s identify rate-limit even on a full 12-photo batch and
// don't blast mobile browsers with 12 simultaneous ~1 MB uploads.
export const BULK_CONCURRENCY = 3;

// Same maxW→profile mapping used by Camera.jsx for legacy callers.
const compressDataURL = (dataUrl, maxW = 1280) => {
  if (maxW <= 300) return compressImage(dataUrl, { profile: "thumb" });
  if (maxW <= 1024) return compressImage(dataUrl, { profile: "quickRetry" });
  return compressImage(dataUrl, { profile: "photo" });
};

/**
 * useBulkIdentify — extracts every piece of state and every async worker
 * needed to drive the "upload N photos, identify them one-by-one, tag them
 * together" flow out of Camera.jsx.
 *
 * Consumers only need to:
 *   1. Render UI from the returned `state` (queue, progress flags, tag values)
 *   2. Wire user actions to the returned `actions` (`start`, `continueNext`,
 *      `cancel`, `reset`, `setBulkTag`, `appendBulkTag`)
 *   3. Optionally call `preparePendingBatch(files)` from the file-picker
 *      when the user selects more than MAX_BULK photos at once.
 *
 * All heavy async work (identify, retry-on-transient, bulk tag apply) lives
 * here so the component tree stays declarative.
 */
export function useBulkIdentify({ includeLocation = false } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkNow: checkMilestones } = useMilestonesContext();
  const { isTransientServerFailure, identifyWithOneRetry, queueTransientFailure } = useIdentifyRetryQueue();

  const [bulkQueue, setBulkQueue] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  // Wall-clock timestamp when the current batch's identify loop began.
  // Powers the elapsed / ETA readout in the Camera bulk view.
  const [bulkStartedAt, setBulkStartedAt] = useState(null);
  const [trimmed, setTrimmed] = useState([]);
  const [pendingBatch, setPendingBatch] = useState([]);
  const [bulkTag, setBulkTag] = useState("");
  const [bulkTagApplied, setBulkTagApplied] = useState(false);
  const [knownTags, setKnownTags] = useState([]);

  // Mirror bulkTag in a ref so the async loop reads the LATEST value the
  // user typed even if they change the tag mid-run.
  const bulkTagRef = useRef("");
  useEffect(() => { bulkTagRef.current = bulkTag; }, [bulkTag]);

  // Mirror includeLocation for the same reason — async loop must observe
  // toggle changes that happen while the batch is running.
  const includeLocationRef = useRef(includeLocation);
  useEffect(() => { includeLocationRef.current = includeLocation; }, [includeLocation]);

  const bulkCancelRef = useRef(false);

  const refreshKnownTags = useCallback(async () => {
    try {
      const { data } = await plants.tags();
      setKnownTags((data?.items || []).map((r) => r.tag));
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { refreshKnownTags(); }, [refreshKnownTags]);

  const appendBulkTag = useCallback((tag) => {
    const clean = String(tag || "").trim();
    if (!clean) return;
    setBulkTag((prev) => {
      const existing = parseTagList(prev);
      const already = existing.some((x) => x.toLowerCase() === clean.toLowerCase());
      if (already) return prev;
      return existing.length ? `${prev.replace(/[\s,]+$/, "")}, ${clean}` : clean;
    });
    setBulkTagApplied(false);
  }, []);

  const start = useCallback(async (files) => {
    bulkCancelRef.current = false;
    const initial = [];
    for (const file of files) {
      const qid = `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      try {
        const raw = await readFileAsDataURL(file);
        const thumb = await compressDataURL(raw, 240);
        // Extract EXIF from the FULL-resolution raw before we downscale for
        // the queue thumbnail. Client-side compression strips EXIF, so
        // reading it here is our only chance.
        const dateTaken = safeExifDateTaken(raw);
        initial.push({ qid, name: file.name, thumb, raw, date_taken: dateTaken, status: "pending" });
      } catch {
        initial.push({ qid, name: file.name, thumb: null, raw: null, date_taken: null, status: "error", error: t("camera.couldNotRead") });
      }
    }
    setBulkQueue(initial); setBulkRunning(true); setBulkDone(false); setBulkStartedAt(Date.now());

    // Bounded concurrency: process up to CONCURRENCY items in parallel.
    // Chosen small so we stay under the per-user 30/60s identify rate-limit
    // even on a full 12-photo batch and don't overwhelm mobile browsers with
    // simultaneous ~1 MB uploads. Wall-time for 5 photos drops from ~50-60s
    // (strictly serial) to ~20-25s (three at a time).
    const CONCURRENCY = BULK_CONCURRENCY;
    let successes = 0, failures = 0, cancelled = 0, queued = 0;
    const taggableScanIds = [];
    // Track per-item durations for both the in-batch ETA-blending logic
    // (via bulkQueue state) and the cross-session p50 store.
    const finishedDurations = [];

    const processOne = async (i) => {
      if (bulkCancelRef.current) return; // outer sweep marks pending → error
      const item = initial[i];
      if (item.status === "error") { failures++; return; }
      const startedAt = Date.now();
      setBulkQueue((q) => q.map((it, idx) => idx === i ? { ...it, status: "processing", startedAt } : it));
      let compact = null;
      let loc = null;
      try {
        compact = await compressDataURL(item.raw, 1280);
        loc = includeLocationRef.current ? await getLocationOnce() : null;
        const { data } = await identifyWithOneRetry(() => plants.identify(compact, true, loc, item.date_taken || null));
        const durationMs = Date.now() - startedAt;
        finishedDurations.push(durationMs);
        setBulkQueue((q) => q.map((it, idx) => idx === i
          ? { ...it, status: "done", scan_id: data.scan_id, name: data.identification?.common_name || it.name, raw: null, durationMs }
          : it));
        if (data?.scan_id) taggableScanIds.push(data.scan_id);
        successes++;
      } catch (e) {
        const durationMs = Date.now() - startedAt;
        if (isTransientServerFailure(e) && compact) {
          const draft = await queueTransientFailure(compact, loc, { showToast: false });
          if (draft?.scan_id) {
            setBulkQueue((q) => q.map((it, idx) => idx === i
              ? { ...it, status: "queued", scan_id: draft.scan_id, raw: null, durationMs }
              : it));
            taggableScanIds.push(draft.scan_id);
            queued++;
            return;
          }
        }
        const msg = e?.response?.data?.detail || t("camera.failed");
        setBulkQueue((q) => q.map((it, idx) => idx === i ? { ...it, status: "error", error: msg, raw: null, durationMs } : it));
        failures++;
      }
    };

    // Worker-pool pattern: each worker pulls the next available index from a
    // shared counter until the batch is drained. Guarantees ≤ CONCURRENCY
    // concurrent Gemini calls in flight and processes items in a
    // predictable head-to-tail order.
    let nextIndex = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, initial.length) }, async () => {
      while (true) {
        if (bulkCancelRef.current) return;
        const idx = nextIndex++;
        if (idx >= initial.length) return;
        await processOne(idx);
      }
    });
    await Promise.all(workers);

    // Cancellation sweep: mark any items that never started as errored.
    if (bulkCancelRef.current) {
      setBulkQueue((q) => q.map((it) => it.status === "pending"
        ? { ...it, status: "error", error: t("camera.cancelled"), raw: null } : it));
      cancelled = initial.filter((it, idx) => idx >= nextIndex && it.status !== "error").length;
    }
    setBulkRunning(false); setBulkDone(true);
    // Persist this batch's real per-item durations to the rolling p50 store
    // so future ETAs self-calibrate to the user's typical latency.
    recordIdentifyDurations(finishedDurations);
    // A bulk run can flip several milestones at once (10th native, 25 total,
    // first keystone, …). Fire a single milestone check once the batch is
    // done — server ranks the response by rarity so the sheet queue shows
    // the most special one first. Only bother when at least one succeeded.
    if (successes > 0) {
      checkMilestones();
    }
    if (successes > 0) toast.success(successes === 1 ? t("camera.toastIdentifiedOne") : t("camera.toastIdentifiedMany", { n: successes }));
    if (queued > 0) {
      toast.message(queued === 1 ? t("camera.toastBulkQueuedOne") : t("camera.toastBulkQueuedMany", { n: queued }), {
        description: t("camera.toastBulkQueuedDesc"),
        duration: 8000,
        action: {
          label: t("camera.viewInGallery"),
          onClick: () => navigate("/gallery"),
        },
      });
    }
    if (failures > 0) toast.error(failures === 1 ? t("camera.toastImageFailOne") : t("camera.toastImageFailMany", { n: failures }));
    if (cancelled > 0) toast.message(t("camera.toastCancelledRem", { n: cancelled }));

    // Apply the batch tag to every scan that made it in (identified OR queued).
    // Read from the ref so late edits to the tag input still count.
    const tags = parseTagList(bulkTagRef.current);
    if (tags.length > 0 && taggableScanIds.length > 0) {
      setBulkTagApplied(true);
      try {
        await plants.bulkTag(taggableScanIds, tags, []);
        toast.success(t("camera.bulkTagApplied", { count: taggableScanIds.length, tag: tags.join(", ") }));
        refreshKnownTags();
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("camera.bulkTagFail"));
        setBulkTagApplied(false);
      }
    }
  }, [t, navigate, identifyWithOneRetry, isTransientServerFailure, queueTransientFailure, refreshKnownTags, checkMilestones]);

  const reset = useCallback(() => {
    setBulkQueue([]); setBulkDone(false); setBulkRunning(false); setBulkStartedAt(null); setTrimmed([]); setPendingBatch([]);
    setBulkTag(""); setBulkTagApplied(false);
    bulkCancelRef.current = false;
  }, []);

  const cancel = useCallback(() => { bulkCancelRef.current = true; }, []);

  const continueNext = useCallback(async () => {
    if (pendingBatch.length === 0) return;
    const nextSlice = pendingBatch.slice(0, MAX_BULK);
    const remainder = pendingBatch.slice(MAX_BULK);
    setPendingBatch(remainder);
    setTrimmed(remainder.map((f) => f.name));
    // Reset the visible queue but preserve bulkTag for the next batch.
    setBulkQueue([]); setBulkDone(false); setBulkRunning(false); setBulkTagApplied(false);
    bulkCancelRef.current = false;
    await start(nextSlice);
  }, [pendingBatch, start]);

  /**
   * Called from the file-picker when the user selects a list larger than
   * MAX_BULK. Splits into the immediate slice + a held remainder that the
   * "Continue with next N" button will drain.
   */
  const preparePendingBatch = useCallback((accepted) => {
    const trimmedSlice = accepted.slice(0, MAX_BULK);
    const overflow = accepted.slice(MAX_BULK);
    if (overflow.length > 0) {
      setTrimmed(overflow.map((f) => f.name));
      setPendingBatch(overflow);
      toast.message(t("camera.batchHeldToast", { n: overflow.length }));
    } else {
      setTrimmed([]);
      setPendingBatch([]);
    }
    return trimmedSlice;
  }, [t]);

  return {
    state: {
      bulkQueue,
      bulkRunning,
      bulkDone,
      bulkStartedAt,
      trimmed,
      pendingBatch,
      bulkTag,
      bulkTagApplied,
      knownTags,
    },
    actions: {
      start,
      continueNext,
      cancel,
      reset,
      setBulkTag,
      setBulkTagApplied,
      appendBulkTag,
      preparePendingBatch,
    },
  };
}
